import {
  Button,
  Stack,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LEGACY_ONLY_SCANNERS,
  SG_SPECIAL_SCANNERS,
  SG_STOCK_SCANNERS,
} from 'config';
import { useEffect, useMemo, useCallback } from 'react';
import { ScannerID, ScannerListItem } from 'types/equityhub';
import useSynthOi from 'hooks/equityhub/useSynthOi';
import OptionsDropdownSelector from 'components/shared/OptionsDropdownSelector';

interface ScannersSectionProps {
  activeScanner: ScannerID | undefined;
  setActiveScanner: (s: ScannerID | undefined) => void;
}

const ScannersSection = ({
  activeScanner,
  setActiveScanner,
}: ScannersSectionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSynthOI } = useSynthOi();

  // Reset to no scanner when switching to SynthOI if dark pool scanner is selected
  useEffect(() => {
    if (isSynthOI) {
      setActiveScanner(undefined);
    }
  }, [isSynthOI, setActiveScanner]);

  const sgScanners = useMemo(() => {
    return isSynthOI
      ? SG_SPECIAL_SCANNERS.filter(
          (s) => !LEGACY_ONLY_SCANNERS.includes(s.scanner),
        )
      : SG_SPECIAL_SCANNERS;
  }, [isSynthOI]);

  const onScannerSelectChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      const selectedScanner = event.target.value as ScannerID;
      if (activeScanner === selectedScanner) {
        setActiveScanner(undefined);
      } else {
        setActiveScanner(selectedScanner);
      }
    },
    [setActiveScanner, activeScanner],
  );

  // Helper function to check if active scanner is in a scanner list and get the value
  const getScannerValue = useCallback(
    (scannerList: ScannerListItem[]) => {
      const isActive = scannerList.some(
        (scanner) => scanner.scanner === activeScanner,
      );
      return isActive ? (activeScanner ?? '') : '';
    },
    [activeScanner],
  );

  // Helper function to create scanner options
  const createScannerOptions = useCallback((scannerList: ScannerListItem[]) => {
    return scannerList.map((scanner) => ({
      label: scanner.label.name,
      value: scanner.scanner,
      icon: scanner.label.icon,
    }));
  }, []);

  return (
    <Stack
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        width: '100%',
        alignItems: 'center',
      }}
    >
      <OptionsDropdownSelector
        label="SG Scanners"
        value={getScannerValue(sgScanners)}
        options={createScannerOptions(sgScanners)}
        isMultiple={false}
        onChange={onScannerSelectChange}
        sx={{ minWidth: '160px' }}
      />
      <OptionsDropdownSelector
        label="Stock Scanners"
        value={getScannerValue(SG_STOCK_SCANNERS)}
        options={createScannerOptions(SG_STOCK_SCANNERS)}
        isMultiple={false}
        onChange={onScannerSelectChange}
        sx={{ minWidth: '160px' }}
      />
      <Button
        size="small"
        sx={{
          textTransform: 'capitalize',
          fontSize: isMobile ? 13 : 13,
          color: theme.palette.text.primary,
          minWidth: 'fit-content',
        }}
        onClick={() => setActiveScanner(undefined)}
      >
        All Symbols
      </Button>
    </Stack>
  );
};

export default ScannersSection;
